import { ClientHandleSettingsController } from './clientHandleSettings';
/** Standalone Settings uses existing form styles and updates fields without replacing their focus. */
export class ClientHandleSettingsPanel {
    constructor() {
        this.root = null;
        this.controller = new ClientHandleSettingsController(state => this.update(state));
    }
    setContainer(root) {
        const changed = this.root !== root;
        this.root = root;
        this.controller.setActive(Boolean(root?.isConnected));
        if (root && changed)
            this.create(root);
        this.update(this.controller.snapshot());
    }
    checkContext() {
        this.controller.setActive(Boolean(this.root?.isConnected));
        this.update(this.controller.snapshot());
    }
    create(root) {
        const doc = root.ownerDocument;
        const element = (tag, className, text = '') => {
            const node = doc.createElement(tag);
            node.className = className;
            node.textContent = text;
            return node;
        };
        const title = element('div', 'settings-group-title', 'Public handle');
        const help = element('div', 'settings-help', 'Choose a name people can use to find you. Visibility controls who can find your @handle; your assigned number stays separate.');
        help.id = 'client-handle-help';
        const message = element('div', 'settings-status');
        message.id = 'client-handle-status';
        message.setAttribute('role', 'status');
        message.setAttribute('aria-live', 'polite');
        const row = element('div', 'settings-row'), label = element('label', 'settings-label', 'Handle');
        label.htmlFor = 'settings-handle';
        const input = element('input', 'settings-input');
        input.id = 'settings-handle';
        input.type = 'text';
        input.placeholder = 'yourname';
        input.autocomplete = 'off';
        input.autocapitalize = 'none';
        input.spellcheck = false;
        input.setAttribute('aria-describedby', 'client-handle-help client-handle-status');
        row.append(label, input);
        const choices = element('div', 'settings-row'), visibilityLabel = element('label', 'settings-label', 'Handle visibility');
        visibilityLabel.htmlFor = 'settings-handle-visibility';
        const select = element('select', 'settings-select');
        select.id = 'settings-handle-visibility';
        const options = [['public', 'Public'], ['friends_of_friends', 'Friends of friends'], ['friends_only', 'Friends only']];
        for (const [value, label] of options) {
            const option = element('option', '', label);
            option.value = value;
            select.append(option);
        }
        choices.append(visibilityLabel, select);
        const controls = element('div', 'settings-actions'), save = element('button', 'settings-btn', 'Save handle'), reload = element('button', 'settings-btn secondary', 'Reload saved handle');
        save.type = reload.type = 'button';
        save.id = 'client-handle-save';
        reload.id = 'client-handle-reload';
        controls.append(save, reload);
        root.replaceChildren(title, help, message, row, choices, controls);
        const current = () => this.root === root && root.isConnected;
        input.addEventListener('input', () => { if (current())
            this.controller.actions().edit({ handle: input.value }); });
        select.addEventListener('change', () => {
            const choice = options.find(([value]) => value === select.value);
            if (current() && choice)
                this.controller.actions().edit({ visibility: choice[0] });
        });
        // Capture the visit when controls are presented. A replaced account's DOM cannot submit its old draft.
        save.addEventListener('click', () => { if (current())
            this.saveAction?.(); });
        reload.addEventListener('click', () => { if (current())
            this.reloadAction?.(); });
        input.addEventListener('keydown', event => { if (current() && event.key === 'Enter') {
            event.preventDefault();
            this.saveAction?.();
        } });
    }
    update(state) {
        const root = this.root;
        if (!root?.isConnected)
            return;
        const input = root.querySelector('#settings-handle'), select = root.querySelector('#settings-handle-visibility');
        const save = root.querySelector('#client-handle-save'), reload = root.querySelector('#client-handle-reload'), message = root.querySelector('#client-handle-status');
        if (!input || !select || !save || !reload || !message)
            return;
        const values = { handle: '', visibility: 'public', ...state.confirmed, ...state.edits };
        if (input.value !== values.handle)
            input.value = values.handle;
        if (select.value !== values.visibility)
            select.value = values.visibility;
        save.disabled = Boolean(state.busy || state.needsReload || !state.confirmed);
        save.textContent = state.busy === 'save' ? 'Saving…' : 'Save handle';
        save.setAttribute('aria-busy', String(state.busy === 'save'));
        reload.disabled = Boolean(state.busy);
        reload.textContent = state.confirmed ? 'Reload saved handle' : 'Retry loading handle';
        message.textContent = state.message;
        message.className = `settings-status ${state.tone}`;
        input.setAttribute('aria-invalid', String(state.tone === 'error' && state.message.startsWith('Use 3')));
        const actions = this.controller.actions();
        this.saveAction = actions.save;
        this.reloadAction = actions.reload;
    }
}
